'use client';

import { type ReactElement, useId } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

/**
 * @Lex identity avatar for group chat (#2607). Renders the Lexomat brand mark
 * (gradient tile + white glyph) inside the SAME circular Avatar frame used for
 * member avatars, so @Lex reads as a visually consistent participant — on
 * completed assistant turns, while streaming, and in the active bar.
 *
 * The mark is inlined at `h-full w-full` (not the fixed-size Logo component) so
 * the gradient tile fills the avatar's square and is clipped by the Avatar's
 * rounded-full to a PROPER circle at any size. Gradient reads the active
 * palette's `--gradient-start/-end`, matching the sidebar logo.
 */
export function LexAvatar({ className }: { className?: string }): ReactElement {
  // Unique gradient id per instance so multiple LexAvatars don't collide.
  const gradientId = useId();
  return (
    <Avatar className={cn('h-7 w-7', className)}>
      <AvatarFallback className='bg-transparent p-0'>
        <svg
          viewBox='0 0 300 300'
          fill='none'
          className='h-full w-full'
          aria-hidden='true'
        >
          <path
            d='M0 50.4425C0 22.5839 22.5839 0 50.4425 0H249.558C277.416 0 300 22.5839 300 50.4425V249.558C300 277.416 277.416 300 249.558 300H50.4425C22.5839 300 0 277.416 0 249.558V50.4425Z'
            fill={`url(#${gradientId})`}
          />
          <path
            d='M57.8095 255.345C84.9555 213.717 92.9201 176.549 95.575 140.708C98.3206 103.641 92.9581 51.8104 88.9378 31.8584C99.9701 56.5008 115.46 100.858 148.672 134.071C189.823 175.221 212.389 184.513 254.309 200.442C207.079 195.133 173.894 201.502 151.327 207.08C128.761 212.658 86.2829 225.664 57.8095 255.345Z'
            fill='white'
          />
          <defs>
            <linearGradient
              id={gradientId}
              x1='49.1151'
              y1='300'
              x2='316.252'
              y2='15.2462'
              gradientUnits='userSpaceOnUse'
            >
              <stop style={{ stopColor: 'var(--gradient-start)' }} />
              <stop offset='1' style={{ stopColor: 'var(--gradient-end)' }} />
            </linearGradient>
          </defs>
        </svg>
      </AvatarFallback>
    </Avatar>
  );
}
